import type { ReactElement } from 'react'
import { useFormContext } from 'react-hook-form'
import { format, parseISO } from 'date-fns'
import { CalendarDays } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { CostInput, formatCostFromNumber, parseCostToNumber } from '@/shared/components/ui/CostInput'
import type { CreateInvoiceRequest } from '../schemas/invoicesSchema'

interface InvoiceFormProps {
  formId: string
  onSubmit: (values: CreateInvoiceRequest) => void | Promise<void>
}

export function InvoiceForm({ formId, onSubmit }: InvoiceFormProps): ReactElement {
  const form = useFormContext<CreateInvoiceRequest>()

  return (
    <form
      id={formId}
      onSubmit={form.handleSubmit(onSubmit)}
      className="space-y-4"
    >
        <FormField
          control={form.control}
          name="invoiceNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Invoice Number</FormLabel>
              <FormControl>
                <Input placeholder="FV/2026/02/001" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="counterparty"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Counterparty</FormLabel>
              <FormControl>
                <Input placeholder="Company name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="saleDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Sale Date</FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full px-3 py-2 h-9 flex items-center justify-start text-left font-normal',
                        !field.value && 'text-muted-foreground',
                      )}
                    >
                      <CalendarDays className="w-4 h-4 mr-2 text-muted-foreground" />
                      <span>
                        {field.value
                          ? format(parseISO(field.value), 'dd MMM yyyy')
                          : 'Pick a date'}
                      </span>
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={field.value ? parseISO(field.value) : undefined}
                    onSelect={date => {
                      field.onChange(date ? format(date, 'yyyy-MM-dd') : '')
                    }}
                    {...(field.value ? { defaultMonth: parseISO(field.value) } : {})}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <CostInput
          label="Net Amount"
          costValue={formatCostFromNumber(form.watch('netAmount'))}
          onCostChange={val => {
            const num = parseCostToNumber(val)
            form.setValue('netAmount', num ?? 0, { shouldValidate: true })
          }}
          currencyValue={form.watch('currency')}
          onCurrencyChange={val => form.setValue('currency', val, { shouldValidate: true })}
        />

        <CostInput
          label="Gross Amount"
          costValue={formatCostFromNumber(form.watch('grossAmount'))}
          onCostChange={val => {
            const num = parseCostToNumber(val)
            form.setValue('grossAmount', num ?? 0, { shouldValidate: true })
          }}
          currencyValue={form.watch('currency')}
          onCurrencyChange={val => form.setValue('currency', val, { shouldValidate: true })}
        />

        <FormField
          control={form.control}
          name="vatRate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>VAT Rate (%)</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="23.00"
                  value={field.value ?? ''}
                  onChange={e => {
                    const val = e.target.value
                    field.onChange(val === '' ? null : parseFloat(val))
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
    </form>
  )
}
